/**
 * Seven rows of twenty-four: when the work actually comes in.
 *
 * A table rather than a chart, and hand-rolled rather than a library, for the reason the map
 * is loaded lazily — this console is a workstation somebody opens at six in the morning on
 * whatever hardware the office had, and a heatmap is forty lines of CSS. A table is also the
 * only shape of this that a screen reader can read: every cell announces its day, its hour,
 * its count and what went unfilled, because a colour with no number behind it is not a figure
 * anybody can act on.
 *
 * The shade is an inline `--shade` between 0 and 1; `DemandHeatmap.css` turns it into a mix
 * of the firm's accent and the card it sits on. It is set here rather than there because only
 * this side knows the busiest hour of the period on screen.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import type { DemandReport } from './api';
import './DemandHeatmap.css';

const hours = Array.from({ length: 24 }, (_, hour) => hour);

/** Sunday is zero in the data; the row order here is the firm's own week start. */
function weekOrder(weekStartsOn: number): number[] {
  return Array.from({ length: 7 }, (_, index) => (index + weekStartsOn) % 7);
}

/** `08:00`, the way an hour column is labelled and read aloud. */
function oClock(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function DemandHeatmap({
  report,
  weekStartsOn,
}: {
  report: DemandReport;
  weekStartsOn: number;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  // The busiest hour of the period, so the ramp uses its whole range rather than squeezing
  // an ordinary Tuesday into the bottom two shades. One, at least, so a quiet period is not
  // a division by zero.
  const busiest = Math.max(1, ...report.cells.map((cell) => cell.orders));
  const cells = new Map(report.cells.map((cell) => [`${cell.dayOfWeek}:${cell.hour}`, cell]));

  return (
    <table className="heat">
      <thead>
        <tr>
          <th scope="col" className="heat-pin" />
          {hours.map((hour) => (
            <th key={hour} scope="col">
              {String(hour).padStart(2, '0')}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {weekOrder(weekStartsOn).map((day) => (
          <tr key={day}>
            <th scope="row" className="heat-pin">
              {formats.weekday(day)}
            </th>

            {hours.map((hour) => {
              const cell = cells.get(`${day}:${hour}`);
              const orders = cell?.orders ?? 0;
              const unfilled = cell?.unfilled ?? 0;

              const sentence = t('reports.demand.cell', {
                day: formats.weekday(day),
                hour: oClock(hour),
                orders,
                unfilled,
              });

              return (
                <td
                  key={hour}
                  className={unfilled > 0 ? 'heat-cell heat-unfilled' : 'heat-cell'}
                  style={
                    {
                      '--shade': orders === 0 ? 0 : 0.2 + (0.8 * orders) / busiest,
                    } as CSSProperties
                  }
                  title={sentence}
                >
                  <span className="sr-only">{sentence}</span>
                  <span aria-hidden="true">{orders === 0 ? '' : formats.count(orders)}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
